import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Text, Alert, AlertIcon, AlertTitle, AlertDescription, Skeleton, useDisclosure } from '@chakra-ui/react';
import { useRouteMatch } from 'react-router-dom';

import { ReactComponent as AddIcon } from '../../icons/add.svg';

import { addAutoformTableRow } from '../../store/autoform/autoformApi';
import { copyAutoformRecords as copyAutoformRecordsService } from '../../services/autoforms';

import useCommonToast from '../../hooks/useCommonToast';
import useAutoformTableMetadata from './hooks/useAutoformTableMetadata';
import useAutoformLoadTableData from './hooks/useAutoformLoadTableData';
import useAutoformTableData from './hooks/useAutoformTableData';
import useAutoformMetadata from './hooks/useAutoformMetadata';
import useHistoryPush from '../../hooks/useHistoryPush';
import { getDtoFromDataValueObject } from './utils/autoformUtils';

import AutoformAddRecordModal from './AutoformAddRecordModal';
import VirtualisedItemList from './VirtualisedItemList';
import LayoutSidebar from '../layout/LayoutSidebar';
import AddItemPortfolios from '../../components/utils/AddItemPortfolios';
import AddItemButton from '../../components/utils/AddItemButton';
import { addItemToPortfolios } from 'services/autoforms';
import AutoformIconNode from './utils/AutoformIconNode';
import ItemLinkWrapper from '../layout/ItemLinkWrapper';
import ItemListWrapper from '../layout/ItemListWrapper';

const SidebarItemListContainer = ({ schemaCode, tableId }) => {
    let { url } = useRouteMatch();

    const dispatch = useDispatch();
    const historyPush = useHistoryPush();
    const { data: items } = useAutoformTableData(tableId);

    const addToPortfolioModal = useDisclosure();
    const addItemModal = useDisclosure();
    const toast = useCommonToast();
    const intl = useIntl();

    const { metadata, itemTypeId } = useAutoformMetadata();
    const {
        primaryKeyColumnName,
        sortedColumns,
        metadata: tableMetadata,
        selectedItem,
    } = useAutoformTableMetadata(tableId);
    const { isLoading, loadingError, reLoadOnCopy } = useAutoformLoadTableData(tableId);
    const descriptionColumnName = sortedColumns.length > 0 ? sortedColumns[0].name : '';

    const autoformItemType = useMemo(() => {
        if (itemTypeId) return true;

        return false;
    }, [itemTypeId]);

    const selectItem = (itemId) => historyPush(`${url}/${itemId}`);

    const [search, setSearch] = useState('');
    const [temporaryNode, setTemporaryNode] = useState(null);

    const sortedResults = useMemo(() => {
        return items.sort((item1, item2) =>
            `${item1[descriptionColumnName]}`.localeCompare(`${item2[descriptionColumnName]}`)
        );
    }, [items, descriptionColumnName]);

    const memoizedResults = useMemo(() => {
        const term = search.trimStart().toLowerCase();
        const filterList = sortedResults.filter((list) =>
            `${list[descriptionColumnName]}`.toLowerCase().includes(term)
        );
        return filterList;
    }, [sortedResults, search, descriptionColumnName]);

    const copiedItemIndex = useMemo(() => {
        return memoizedResults.findIndex((item) => item[primaryKeyColumnName] === selectedItem);
    }, [memoizedResults, selectedItem, primaryKeyColumnName]);

    const noSearchResults = search.trim().length > 0 && memoizedResults.length === 0;

    const onAddRecord = async (record) => {
        const parameters = metadata.parameters.map((parameter) => parameter.value);
        const addedRecord = await dispatch(
            addAutoformTableRow({
                schemaCode,
                tableId,
                record: getDtoFromDataValueObject(record, tableMetadata),
                parameters,
            })
        ).unwrap();
        selectItem(addedRecord.data[primaryKeyColumnName]);
        addItemModal.onClose();
        toast(intl.formatMessage({ id: 'common_item_creation_success' }, { type: '' }));
    };

    const onAutoformCopy = async (autoformItems) => {
        toast({ status: 'info', message: intl.formatMessage({ id: 'common_copy_btn_progress_message' }) });
        try {
            const response = await copyAutoformRecordsService({ itemId: autoformItems[primaryKeyColumnName] });
            await Promise.all([reLoadOnCopy(), selectItem(response.id)]);
        } catch (error) {
            if (error.response && error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    const onAddToPortfolioModalOpen = (item) => {
        setTemporaryNode(item);
        addToPortfolioModal.onOpen();
    };

    const closeAddToPortfolioModal = () => {
        addToPortfolioModal.onClose();
        setTemporaryNode(null);
    };

    const getAddItemToPortfolioSuccessMessage = (item, portfolios) => {
        if (portfolios.length === 1) {
            return intl.formatMessage(
                { id: 'common_add_item_success_message' },
                { item: item[descriptionColumnName], destination: portfolios[0].properties.name }
            );
        }

        return intl.formatMessage(
            { id: 'common_add_item_to_multiple_portfolios_success_message' },
            { item: item[descriptionColumnName], n: portfolios.length }
        );
    };

    const onAddItemToPortfolioSuccess = (selectedPortfolios) => {
        toast(getAddItemToPortfolioSuccessMessage(temporaryNode, selectedPortfolios));
        closeAddToPortfolioModal();
    };

    return (
        <LayoutSidebar
            itemType={'battery'}
            selected={selectItem}
            titleMessage={metadata.formLabel} // TODO provide title for side heading
            accordionPlaceholderId={'autoform_select_item'} // TODO How do we provide translations for these labels
            searchPlaceholderId={'common_search_items'} // TODO How do we provide translations for these labels
            searchCallback={setSearch}
        >
            <ItemListWrapper>
                <Box px={6} overflowY="auto">
                    {tableMetadata.canCreate && (
                        <AddItemButton leftIcon={<AddIcon />} onClick={addItemModal.onOpen}>
                            <Text size="sm" color="gray.900" pl={1} pt={1}>
                                <FormattedMessage id="autoform_add_new_item" />
                            </Text>
                        </AddItemButton>
                    )}
                    {isLoading ? (
                        <Skeleton w="100%" mt={6}>
                            <Box h="34px">
                                <FormattedMessage id="common_loading" />
                            </Box>
                        </Skeleton>
                    ) : loadingError ? (
                        <Alert status="error">
                            <AlertIcon />

                            <Box>
                                <AlertTitle textTransform="capitalize">
                                    <FormattedMessage id="common_error" />
                                </AlertTitle>

                                <AlertDescription>
                                    <FormattedMessage id="common_loading_error" values={{ items: 'items' }} />
                                </AlertDescription>
                            </Box>
                        </Alert>
                    ) : noSearchResults ? (
                        <Alert status="info">
                            <AlertIcon />

                            <FormattedMessage id="common_no_search_results" />
                        </Alert>
                    ) : (
                        <VirtualisedItemList
                            items={memoizedResults}
                            onAutoformCopy={onAutoformCopy}
                            itemIndex={copiedItemIndex}
                            autoformItemType={autoformItemType}
                            onAddToPortfolioModalOpen={onAddToPortfolioModalOpen}
                            renderItems={(virtualItem) => (
                                <ItemLinkWrapper
                                    key={memoizedResults[virtualItem.index][primaryKeyColumnName]}
                                    itemId={memoizedResults[virtualItem.index][primaryKeyColumnName]}
                                    url={url}
                                    isMaster={memoizedResults[virtualItem.index]?.SECURITYID}
                                    icon={<AutoformIconNode itemTypeId={+itemTypeId?.value} />}
                                    label={memoizedResults[virtualItem.index][descriptionColumnName]}
                                    onClick={() => selectItem(memoizedResults[virtualItem.index][primaryKeyColumnName])}
                                />
                            )}
                        />
                    )}
                </Box>
            </ItemListWrapper>

            {addItemModal.isOpen && (
                <AutoformAddRecordModal
                    tableId={tableId}
                    itemType="Item"
                    isOpen={addItemModal.isOpen}
                    onClose={addItemModal.onClose}
                    onCreateRecord={onAddRecord}
                    onAddRecord={() => {}}
                />
            )}
            {addToPortfolioModal.isOpen && (
                <AddItemPortfolios
                    onClose={closeAddToPortfolioModal}
                    source={temporaryNode}
                    itemName={temporaryNode[descriptionColumnName]}
                    onAddSuccess={onAddItemToPortfolioSuccess}
                    onSubmit={(id, portfoliosPropertyIds) =>
                        addItemToPortfolios(temporaryNode[primaryKeyColumnName], portfoliosPropertyIds)
                    }
                />
            )}
        </LayoutSidebar>
    );
};

export default SidebarItemListContainer;
